import { Big } from 'big.js'
import { groupBy } from 'lodash'
import { map } from './functional/array.no-deps'
import { dictValues } from './functional/dictionary.no-deps'
import { isOdd } from './functional/number.no-deps'
import { pipe } from './functional/pipe.no-deps'

export function sum(values: Big[]): Big {
    return values.reduce((a, b) => a.add(b), Big(0))
}

export function product(values: Big[]): Big {
    return values.reduce((a, b) => a.times(b), Big(1))
}

export function mean(values: Big[]): Big {
    return sum(values).div(values.length)
}

export function median(values: Big[]): Big {
    const sortedValues = [...values].sort((v1, v2) => v1.cmp(v2))
    const midPoint = (sortedValues.length - 1) / 2

    if (isOdd(sortedValues.length)) {
        return sortedValues[midPoint]
    } else {
        const a = sortedValues[midPoint - 0.5]
        const b = sortedValues[midPoint + 0.5]
        return a.plus(b).div(2)
    }
}

export function mode(values: Big[]): [Big, Big[]] {
    // If empty, then return empty value
    if (values.length === 0) return [null, []]

    // Put into buckets to work out frequencies
    const groups = groupBy(values, (v) => v.toString())

    let currentHighest = -1
    let mode: Big
    let modeArray: Big[] = []

    for (const instances of dictValues(groups)) {
        // If new max, then reset values
        if (instances.length > currentHighest) {
            currentHighest = instances.length
            mode = instances[0]
            modeArray = [instances[0]]
            continue
        }

        // If multi-modal, add to array
        if (instances.length === currentHighest) {
            modeArray.push(instances[0])
            continue
        }
    }

    return [mode, modeArray]
}

export function maximum(values: Big[]): Big {
    if (values.length === 0) return null
    return values.reduce((a, b) => (a.lt(b) ? b : a), values[0])
}

export function minimum(values: Big[]): Big {
    if (values.length === 0) return null
    return values.reduce((a, b) => (a.gt(b) ? b : a), values[0])
}

export function range(values: Big[]): Big {
    if (values.length === 0) return null
    const max = maximum(values)
    const min = minimum(values)
    return max.minus(min)
}

export function stddev(values: Big[]): Big {
    const m = mean(values)

    return pipe(
        values,
        map((v) => v.minus(m).pow(2)),
        mean,
        (v) => v.sqrt()
    )
}
